// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: everything the scratchpad needs from the rest of the app, in one place.
// People and conversations for "@" and "#", sending lines to a chat, reminders for
// due dates, ClickUp task actions, the Claude assistant, and Outlook mail/event
// lookups for pasted links. The rail preview swaps this module for a mock.

import {Routes} from '@app/app/Routes';
import {Endpoints} from '@app/features/app/constants/Endpoints';
import * as PrivateChannelCommands from '@app/features/channel/commands/PrivateChannelCommands';
import Channels from '@app/features/channel/state/Channels';
import {AskKonvoStore, stripSourceLinks} from '@app/features/konvo_ask/state/AskKonvo';
import KonvoCalendar from '@app/features/konvo_calendar/state/KonvoCalendar';
import KonvoMail from '@app/features/konvo_mail/state/KonvoMail';
import KonvoReminders from '@app/features/konvo_reminders/state/KonvoReminders';
import KonvoScratchpad from '@app/features/konvo_scratchpad/state/KonvoScratchpad';
import {openKonvoCreateTask, openKonvoTask} from '@app/features/konvo_tasks/commands/KonvoTaskCommands';
import KonvoTasks from '@app/features/konvo_tasks/state/KonvoTasks';
import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import Navigation from '@app/features/navigation/state/Navigation';
import {transitionTo} from '@app/features/navigation/utils/RouterUtils';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import Users from '@app/features/user/state/Users';
import {getChannelIconURL, getUserAvatarURL} from '@app/features/user/utils/AvatarUtils';
import {ChannelTypes} from '@fluxer/constants/src/ChannelConstants';
import * as SnowflakeUtils from '@fluxer/snowflake/src/SnowflakeUtils';
import {reaction} from 'mobx';

export interface Person {
	id: string;
	name: string;
	avatar: string | null;
}

export interface Conversation {
	id: string;
	name: string;
	kind: 'dm' | 'group' | 'channel';
	guildId: string | null;
}

export interface OutlookCard {
	kind: 'mail' | 'event';
	id: string;
	title: string;
	subtitle: string;
	when: string | null;
	joinUrl: string | null;
}

function matches(haystack: string, q: string): number {
	const h = haystack.toLowerCase();
	if (h === q) return 3;
	if (h.startsWith(q)) return 2;
	if (h.includes(q)) return 1;
	return 0;
}

export function searchPeople(query: string, limit = 6): Array<Person> {
	const q = query.trim().toLowerCase();
	const me = Users.currentUserId;
	return Users.usersList
		.filter((user) => !user.bot && user.id !== me)
		.map((user) => ({
			user,
			score: q ? Math.max(matches(user.displayName, q), matches(user.username, q)) : 1,
		}))
		.filter((entry) => entry.score > 0)
		.sort((a, b) => b.score - a.score || a.user.displayName.localeCompare(b.user.displayName))
		.slice(0, limit)
		.map(({user}) => ({
			id: user.id,
			name: user.displayName,
			avatar: getUserAvatarURL({id: user.id, avatar: user.avatar}),
		}));
}

function conversationOf(channel: {
	id: string;
	type: number;
	name?: string;
	guildId?: string;
	recipientIds?: ReadonlyArray<string>;
}): Conversation | null {
	if (channel.type === ChannelTypes.DM) {
		const other = channel.recipientIds?.find((id) => id !== Users.currentUserId) ?? channel.recipientIds?.[0];
		const user = other ? Users.getUser(other) : null;
		return {id: channel.id, name: user?.displayName ?? channel.name ?? 'Direct message', kind: 'dm', guildId: null};
	}
	if (channel.type === ChannelTypes.GROUP_DM) {
		return {id: channel.id, name: channel.name || 'Group', kind: 'group', guildId: null};
	}
	if (channel.type === ChannelTypes.GUILD_TEXT) {
		return {id: channel.id, name: channel.name ?? 'channel', kind: 'channel', guildId: channel.guildId ?? null};
	}
	return null;
}

export function searchConversations(
	query: string,
	limit = 8,
	kinds?: ReadonlyArray<Conversation['kind']>,
): Array<Conversation> {
	const q = query.trim().toLowerCase();
	const all = [
		...Channels.dmChannels,
		...Channels.allChannels.filter((channel) => channel.type === ChannelTypes.GUILD_TEXT),
	];
	const seen = new Set<string>();
	const out: Array<{conversation: Conversation; score: number}> = [];
	for (const channel of all) {
		if (seen.has(channel.id)) continue;
		seen.add(channel.id);
		const conversation = conversationOf(channel as unknown as Parameters<typeof conversationOf>[0]);
		if (!conversation || (kinds && !kinds.includes(conversation.kind))) continue;
		const score = q ? matches(conversation.name, q) : 1;
		if (score > 0) out.push({conversation, score});
	}
	return out
		.sort((a, b) => b.score - a.score)
		.slice(0, limit)
		.map((entry) => entry.conversation);
}

/** The phone layout: full-screen drawer, All pads first, the keyboard bar instead of the toolbar. */
export function isPhoneLayout(): boolean {
	return MobileLayout.platformMobileDetected || MobileLayout.enabled;
}

export function currentUserId(): string | null {
	return Users.currentUserId ?? null;
}

export function personById(userId: string): Person | null {
	const user = Users.getUser(userId);
	if (!user) return null;
	return {id: user.id, name: user.displayName, avatar: getUserAvatarURL({id: user.id, avatar: user.avatar})};
}

/** "Lerato M." for a DM, the group or channel name otherwise, plus how many people share it. */
export function conversationInfo(
	channelId: string,
): {name: string; kind: Conversation['kind']; members: number} | null {
	const channel = Channels.getChannel(channelId);
	if (!channel) return null;
	const conversation = conversationOf(channel as unknown as Parameters<typeof conversationOf>[0]);
	if (!conversation) return null;
	const members = channel.type === ChannelTypes.GUILD_TEXT ? 0 : (channel.recipientIds?.length ?? 0) + 1;
	return {name: conversation.name, kind: conversation.kind, members};
}

/** What a shared pad card shows about its chat: who, where, and the people in it. */
export interface ConversationCard {
	id: string;
	name: string;
	kind: Conversation['kind'];
	guildId: string | null;
	/** The DM partner's avatar or the group icon; null means draw initials. */
	avatar: string | null;
	/** Everyone who can edit, the current user included (empty for guild channels). */
	members: Array<Person>;
}

function initialsOf(name: string): string {
	const words = name
		.replace(/[^\p{L}\p{N}\s]/gu, '')
		.trim()
		.split(/\s+/)
		.filter(Boolean);
	if (words.length === 0) return '?';
	if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
	return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
}

export {initialsOf};

export function conversationCard(channelId: string): ConversationCard | null {
	const channel = Channels.getChannel(channelId);
	if (!channel) return null;
	const conversation = conversationOf(channel as unknown as Parameters<typeof conversationOf>[0]);
	if (!conversation) return null;
	const me = Users.currentUserId;
	const memberIds =
		channel.type === ChannelTypes.GUILD_TEXT ? [] : [...(channel.recipientIds ?? []), ...(me ? [me] : [])];
	const members = [...new Set(memberIds)]
		.map((id) => personById(id))
		.filter((person): person is Person => person !== null);
	let avatar: string | null = null;
	if (conversation.kind === 'dm') {
		const other = channel.recipientIds?.find((id) => id !== me);
		avatar = other ? (personById(other)?.avatar ?? null) : null;
	} else if (conversation.kind === 'group') {
		avatar = getChannelIconURL({id: channel.id, icon: channel.icon ?? null}) || null;
	}
	return {...conversation, avatar, members};
}

/** The most recently active DMs and groups with people (not bots): where shared pads usually live. */
export function recentConversationIds(limit = 6): Array<string> {
	const out: Array<string> = [];
	for (const channel of Channels.dmChannels) {
		if (channel.type !== ChannelTypes.DM && channel.type !== ChannelTypes.GROUP_DM) continue;
		const others = (channel.recipientIds ?? []).filter((id) => id !== Users.currentUserId);
		if (others.length === 0 || others.every((id) => Users.getUser(id)?.bot)) continue;
		out.push(channel.id);
		if (out.length >= limit) break;
	}
	return out;
}

/** On the phone the pad covers the whole screen, so going to a chat, mail or event closes it. */
function leavePadOnPhone(): void {
	if (isPhoneLayout()) KonvoScratchpad.setOpen(false);
}

export function openPerson(userId: string): void {
	leavePadOnPhone();
	void PrivateChannelCommands.openDMChannel(userId);
}

export function openConversation(conversation: Pick<Conversation, 'id' | 'guildId'>): void {
	leavePadOnPhone();
	transitionTo(Navigation.buildChannelPath(conversation.guildId, conversation.id));
}

export async function sendToConversation(channelId: string, content: string): Promise<void> {
	await MessageCommands.send(channelId, {content, nonce: SnowflakeUtils.fromTimestamp(Date.now())});
}

export async function createReminder(at: Date, note: string): Promise<void> {
	await KonvoReminders.create({remindAt: at, note});
}

/** Cancels reminders that were made for this line (same time and note). */
export async function cancelReminder(at: Date, note: string): Promise<void> {
	await KonvoReminders.fetch();
	const target = at.getTime();
	for (const reminder of KonvoReminders.reminders) {
		const when = new Date(reminder.remind_at).getTime();
		if (Math.abs(when - target) < 60_000 && (reminder.note ?? '') === note) await KonvoReminders.cancel(reminder.id);
	}
}

export function openTask(taskId: string): void {
	openKonvoTask(taskId);
}

export function createTask(initialName: string, onCreated: (task: {id: string; url: string}) => void): void {
	openKonvoCreateTask(initialName, onCreated);
}

export async function completeTask(taskId: string): Promise<void> {
	await KonvoTasks.completeTask(taskId);
}

export function runningTimerTaskId(): string | null {
	return KonvoTasks.runningEntry?.task.id ?? null;
}

export async function toggleTimer(taskId: string): Promise<void> {
	if (runningTimerTaskId() === taskId) await KonvoTasks.stopTimer();
	else await KonvoTasks.startTimer(taskId);
}

export function openExternal(url: string): void {
	void openExternalUrl(url);
}

export function openMail(messageId: string): void {
	leavePadOnPhone();
	KonvoMail.select(messageId);
	transitionTo(Routes.EMAILS);
}

export function openEvent(eventId: string): void {
	leavePadOnPhone();
	transitionTo(`${Routes.CALENDAR}?open=${encodeURIComponent(eventId)}`);
}

const OUTLOOK_HOST = /(^|\.)outlook\.(office365|office|live)\.com$/i;

/** Outlook web links carry the Graph item id; mail and calendar items are told apart by the path param. */
export function parseOutlookLink(url: string): {kind: 'mail' | 'event'; id: string} | null {
	try {
		const parsed = new URL(url);
		if (!OUTLOOK_HOST.test(parsed.hostname)) return null;
		let id: string | null = null;
		for (const [key, value] of parsed.searchParams) {
			if (key.toLowerCase() === 'itemid') id = value;
		}
		if (!id) return null;
		const path = (parsed.searchParams.get('path') ?? '').toLowerCase();
		return {kind: path.includes('calendar') ? 'event' : 'mail', id};
	} catch {
		return null;
	}
}

export async function resolveOutlook(url: string): Promise<OutlookCard | null> {
	const link = parseOutlookLink(url);
	if (!link) return null;
	if (link.kind === 'event') {
		const event = await KonvoCalendar.fetchEvent(link.id);
		if (!event) return null;
		return {
			kind: 'event',
			id: event.id,
			title: event.subject,
			subtitle: event.location || event.organizer || 'Calendar',
			when: event.start,
			joinUrl: event.konvo_join_url ?? event.teams_join_url ?? null,
		};
	}
	const mail = await KonvoMail.loadDetail(link.id);
	if (!mail) return null;
	return {
		kind: 'mail',
		id: mail.id,
		title: mail.subject || '(no subject)',
		subtitle: mail.from_name || mail.from_address || 'Mail',
		when: mail.received_at,
		joinUrl: null,
	};
}

/** Asks the Claude assistant one question and resolves with its reply text. */
export function askAssistant(question: string): Promise<string> {
	const chat = new AskKonvoStore({sharedHistory: true});
	return new Promise((resolve, reject) => {
		const stop = reaction(
			() => chat.answerStatus,
			(status) => {
				if (status === 'done') {
					stop();
					const answer = chat.lastAnswer?.text ?? '';
					chat.dispose();
					resolve(stripSourceLinks(answer).trim());
				} else if (status === 'failed') {
					stop();
					chat.dispose();
					reject(new Error('assistant failed'));
				}
			},
		);
		chat.sendQuestion(question).catch((error) => {
			stop();
			chat.dispose();
			reject(error);
		});
	});
}

export function endpointsAvailable(): boolean {
	return Boolean(Endpoints.KONVO_REMINDERS);
}
